import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import FileLoaderService from '../services/file-loader-service';
import { statuses, triggerTypes } from '../services/file-loader-service';
import { fileStatusChange, fileTriggered } from '../utils/events';

const BEFORE_LOADER_STAGING = [
    statuses.UPLOADING,
    statuses.UPLOADED,
    statuses.SCHEMA_VALIDATED,
    statuses.SHARED_STAGING,
    statuses.SHARED_STAGED,
    statuses.LOADER_STAGING
];

const FileTriggerButton = ( props ) => {
    const { fileId, auth, onFileTriggered, disabled } = props;

    const [ file, setFile ] = useState( null );
    const [ processing, setProcessing ] = useState( false );

    useEffect( () => {
        if ( !fileId ) {
            return;
        }
        const loadFile = async () => {
            try {
                const fileLoader = FileLoaderService( auth.state );
                const fileResponse = await fileLoader.getFile( fileId );
                setFile( fileResponse );
            } catch ( error ) {
                console.error( 'Error fetching file', error.status );
                setFile( null );
            }
        };
        loadFile();

        const handleFileStatusChange = ( id ) => {
            if ( id === fileId ) {
                loadFile();
            }
        };

        fileStatusChange.on( handleFileStatusChange );

        return () => fileStatusChange.off( handleFileStatusChange );

    }, [ fileId, auth?.state?.claims?.company_id ] );

    const triggerData = useMemo( () => {
        let disabled = true;
        let text = 'Complete Load';
        let type = 'NONE';

        if ( !file || file.failedAt ) {
            disabled = true;
        } else if ( BEFORE_LOADER_STAGING.includes( file.status ) ) {
            disabled = true;
        } else if ( file.status === statuses.LOADING ) {
            disabled = true;
        } else if ( file.status === statuses.LOADER_STAGED ) {
            const createdTimeFrame = 1000 * 60 * 60 * 24; // 24 hours
            const created = new Date( Date.now() - createdTimeFrame );
            if ( new Date( file.loaderStagingCompletedAt ).valueOf() < created.valueOf() ) {
                disabled = false;
                text = 'Revalidate';
                type = triggerTypes.REVALIDATE;
            } else {
                disabled = false;
                text = 'Complete Load';
                type = triggerTypes.LOAD;
            }
        } else if ( !( file.loadingStartedAt || file.loadingCompletedAt || file.loadingRequestedAt ) ) {
            disabled = false;
            text = 'Reprocess';
            type = triggerTypes.REPROCESS;
        }

        return {
            disabled,
            text,
            type
        };
    }, [ file ] );

    const processFile = async () => {
        const { type } = triggerData;

        setProcessing( true );

        try {
            const fileLoader = FileLoaderService( auth.state );
            await fileLoader.trigger( fileId, type );
            await new Promise( ( resolve ) => setTimeout( resolve, 1000 ) );
            const fileResponse = await fileLoader.getFile( fileId );
            setFile( fileResponse );
        } catch ( error ) {
            console.error( 'Error triggering file', error.status );
        } finally {
            setProcessing( false );

            if ( typeof onFileTriggered === 'function' ) {
                onFileTriggered();
            }
            fileTriggered.fire( fileId );
        }
    };

    return (
        <Button
            type="primary"
            disabled={ disabled || triggerData.disabled || processing }
            loading={ processing }
            onClick={ processFile }
            icon={ <CheckOutlined /> }
        >
            <span>{ triggerData.text }</span>
        </Button>
    );
};

export default FileTriggerButton;